import { computed, ref } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/lib/utils/vue3compat/vue.js?vue3';
import { defineStore } from '/builds/gitlab/gitlab-ee/node_modules/pinia/dist/pinia.mjs?vue3';
import axios from '/builds/gitlab/gitlab-ee/app/assets/javascripts/lib/utils/axios_utils.js?vue3';
import { HTTP_STATUS_GONE } from '~/lib/utils/http_status';
import { useDiffDiscussions } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/rapid_diffs/stores/diff_discussions.js?vue3';
import { useDiffsView } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/rapid_diffs/stores/diffs_view.js?vue3';

export const useCommitDiffDiscussions = defineStore('commitDiffDiscussions', () => {
  const diffDiscussions = useDiffDiscussions();
  const endpoint = ref('');

  function setDiscussionsEndpoint(url) {
    endpoint.value = url;
  }

  const timelineDiscussions = computed(() => {
    return diffDiscussions.discussionsWithForms.filter(
      (discussion) => !discussion.isForm && !discussion.diff_discussion,
    );
  });

  async function createNewDiscussion(noteData) {
    const {
      data: { discussion },
    } = await axios.post(endpoint.value, { note: noteData });
    diffDiscussions.addDiscussion(discussion);
  }

  async function createImageDiscussion({ position, noteBody }) {
    await createNewDiscussion({ note: noteBody, position });
  }

  async function createLineDiscussion({ discussion: formDiscussion, noteBody, showWhitespace }) {
    const resolvedShowWhitespace = showWhitespace ?? useDiffsView().showWhitespace;
    const position = {
      ...formDiscussion.position,
      ignore_whitespace_change: !resolvedShowWhitespace,
    };
    const {
      data: { discussion },
    } = await axios.post(endpoint.value, {
      note: { note: noteBody, position },
    });
    diffDiscussions.replaceDiscussionForm(formDiscussion, discussion);
  }

  async function replyToDiscussion(discussion, noteText) {
    const {
      data: { discussion: updated },
    } = await axios.post(endpoint.value, {
      in_reply_to_discussion_id: discussion.reply_id,
      note: { note: noteText },
    });
    diffDiscussions.replaceDiscussion(discussion, updated);
  }

  async function saveNote(note, noteText) {
    try {
      const {
        data: { note: updatedNote },
      } = await axios.put(note.path, {
        rapid_diffs: true,
        target_id: note.noteable_id,
        note: { note: noteText },
      });
      diffDiscussions.updateNote(updatedNote);
    } catch (error) {
      if (error.response?.status === HTTP_STATUS_GONE) {
        diffDiscussions.deleteNote(note);
        return;
      }
      throw error;
    }
  }

  async function destroyNote(note) {
    await axios.delete(note.path);
    diffDiscussions.deleteNote(note);
  }

  async function toggleAwardOnNote(note, name) {
    await axios.post(note.toggle_award_path, { name });
    diffDiscussions.toggleAward({ note, award: name });
  }

  return {
    setDiscussionsEndpoint,
    createNewDiscussion,
    createImageDiscussion,
    createLineDiscussion,
    replyToDiscussion,
    saveNote,
    destroyNote,
    toggleAwardOnNote,
    setInitialDiscussions: diffDiscussions.setInitialDiscussions,
    replaceDiscussion: diffDiscussions.replaceDiscussion,
    toggleDiscussionReplies: diffDiscussions.toggleDiscussionReplies,
    expandDiscussionReplies: diffDiscussions.expandDiscussionReplies,
    startReplying: diffDiscussions.startReplying,
    stopReplying: diffDiscussions.stopReplying,
    addNote: diffDiscussions.addNote,
    updateNote: diffDiscussions.updateNote,
    updateNoteTextById: diffDiscussions.updateNoteTextById,
    editNote: diffDiscussions.editNote,
    deleteNote: diffDiscussions.deleteNote,
    addDiscussion: diffDiscussions.addDiscussion,
    deleteDiscussion: diffDiscussions.deleteDiscussion,
    setEditingMode: diffDiscussions.setEditingMode,
    requestLastNoteEditing: diffDiscussions.requestLastNoteEditing,
    toggleAward: diffDiscussions.toggleAward,
    addNewLineDiscussionForm: diffDiscussions.addNewLineDiscussionForm,
    replaceDiscussionForm: diffDiscussions.replaceDiscussionForm,
    removeNewLineDiscussionForm: diffDiscussions.removeNewLineDiscussionForm,
    setDiscussionFormText: diffDiscussions.setDiscussionFormText,
    setNewLineDiscussionFormAutofocus: diffDiscussions.setNewLineDiscussionFormAutofocus,
    setFileDiscussionsHidden: diffDiscussions.setFileDiscussionsHidden,
    expandLineDiscussions: diffDiscussions.expandLineDiscussions,
    collapseDiscussion: diffDiscussions.collapseDiscussion,
    expandDiscussion: diffDiscussions.expandDiscussion,
    discussionsWithForms: computed(() => diffDiscussions.discussionsWithForms),
    findDiscussionsForFile: computed(() => diffDiscussions.findDiscussionsForFile),
    findLinePositionsForFile: computed(() => diffDiscussions.findLinePositionsForFile),
    findLineDiscussionsForPosition: computed(() => diffDiscussions.findLineDiscussionsForPosition),
    findAllImageDiscussionsForFile: computed(
      () =>
        ({ oldPath, newPath }) =>
          diffDiscussions.findAllImageDiscussionsForFile(oldPath, newPath),
    ),
    timelineDiscussions,
  };
});
